import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SERVICES } from "@/lib/services";

export interface PlaceLite {
  id: string;
  name: string;
  slug: string;
  population: number | null;
  place_type: string | null;
  lat?: number | null;
  lon?: number | null;
  province_id?: string | null;
  parent_city_id?: string | null;
}

/** Keys of services switched on in /admin. Falls back to all services. */
export function useActiveServiceKeys() {
  return useQuery({
    queryKey: ["services-active"],
    queryFn: async () => {
      const { data, error } = await supabase.from("services").select("service_key, is_active");
      if (error || !data?.length) return new Set(SERVICES.map((s) => s.key));
      return new Set(data.filter((d) => d.is_active).map((d) => d.service_key));
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** All top-level places in a province (paginated past the 1000-row limit). */
export function useProvincePlaces(provinceId?: string) {
  return useQuery({
    queryKey: ["province-places", provinceId],
    enabled: !!provinceId,
    queryFn: async () => {
      const all: PlaceLite[] = [];
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase
          .from("cities")
          .select("id, name, slug, population, place_type")
          .eq("province_id", provinceId!)
          .eq("is_active", true)
          .is("parent_city_id", null)
          .order("name")
          .range(from, from + 999);
        if (error) throw error;
        all.push(...(data as PlaceLite[]));
        if (!data || data.length < 1000) break;
      }
      return all;
    },
  });
}

/** Nearby top-level places in the same province, closest first. */
export function useNearbyPlaces(place?: { id: string; province_id?: string | null; lat?: number | null; lon?: number | null }) {
  return useQuery({
    queryKey: ["nearby-places", place?.id],
    enabled: !!place?.province_id,
    queryFn: async () => {
      let q = supabase
        .from("cities")
        .select("id, name, slug, population, place_type, lat, lon")
        .eq("province_id", place!.province_id!)
        .eq("is_active", true)
        .is("parent_city_id", null)
        .neq("id", place!.id);
      const hasGeo = place!.lat != null && place!.lon != null;
      if (hasGeo) {
        const d = 0.2;
        q = q.gte("lat", place!.lat! - d).lte("lat", place!.lat! + d).gte("lon", place!.lon! - d).lte("lon", place!.lon! + d);
      } else {
        q = q.order("population", { ascending: false, nullsFirst: false });
      }
      const { data, error } = await q.limit(60);
      if (error) throw error;
      let list = data as PlaceLite[];
      if (hasGeo) {
        list = list
          .map((p) => ({ p, dist: ((p.lat ?? 0) - place!.lat!) ** 2 + ((p.lon ?? 0) - place!.lon!) ** 2 }))
          .sort((a, b) => a.dist - b.dist)
          .map((x) => x.p);
      }
      return list.slice(0, 12);
    },
  });
}

/** Active Dutch provinces only (the site targets the Netherlands). */
export function useNlProvinces() {
  return useQuery({
    queryKey: ["provinces-nl"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("provinces")
        .select("id, name, slug, countries!inner(iso2)")
        .eq("is_active", true)
        .eq("countries.iso2", "NL")
        .order("name");
      if (error) throw error;
      return data as { id: string; name: string; slug: string }[];
    },
  });
}
